import { useState } from "react";
import { CheckCircle2, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { CONSENT_TEXT, SITE } from "@/lib/site";
import { useBases } from "@/hooks/useData";
import { DateField } from "./DateField";

const BRANCHES = ["Navy", "Marine Corps", "Army", "Air Force", "Coast Guard", "Space Force", "DoD civilian / contractor", "Veteran / retired"];
const PROPERTY_TYPES = ["Single Family Residence", "Town House", "Condominium", "Investment Properties", "Land/Lot", "Other"];
const inputCls = "h-11 rounded-md border-input bg-white text-[15px] focus-visible:ring-ring";

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-[13px] font-normal text-neutral-600">{label}</Label>
    {children}
  </div>
);

const BaseSelect = ({ value, onChange, testId }) => {
  const bases = useBases();
  const regions = [...new Set(bases.map((b) => b.region))];
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testId}><SelectValue placeholder="Choose an installation" /></SelectTrigger>
      <SelectContent>
        {regions.map((r) => (
          <SelectGroup key={r}>
            <SelectLabel>{r}</SelectLabel>
            {bases.filter((b) => b.region === r).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
};

const SimpleSelect = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className={inputCls} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

/**
 * Short enquiry form. `extras` adds optional fields:
 * base | branch | direction | reportDate | preferredDate | address | propertyType
 */
export const EnquiryForm = ({
  kind = "contact", listingKey, defaultBaseId, extras = [], messagePlaceholder = "How can we help?", topic,
  submitLabel = "Send", testIdPrefix = "enquiry", dense = false, onSuccess,
}) => {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "", base_id: defaultBaseId || null, consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const tid = (s) => `${testIdPrefix}-${s}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() || !f.email.trim()) return toast.error("Please add your first name and email.");
    if (!f.consent) return toast.error("Please tick the box so we're allowed to reply.");
    setBusy(true);
    try {
      await api.post("/enquiries", { ...f, kind, listing_key: listingKey || null, topic: topic || null });
      setDone(true);
      toast.success(`Thanks, ${f.first_name}! The team will reach out shortly.`);
      onSuccess?.();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-md border border-neutral-200 bg-mist p-6 text-center" data-testid={tid("success")}>
        <CheckCircle2 className="mx-auto h-9 w-9 text-brass" />
        <p className="mt-3 text-lg font-medium text-ink">Request received</p>
        <p className="mt-1 text-[15px] text-neutral-600">We'll be in touch soon. Moving fast? Call us now.</p>
        <a href={SITE.phoneMainHref} className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-white" data-testid={tid("success-call")}>
          <Phone className="h-4 w-4" /> {SITE.phoneMain}
        </a>
      </div>
    );
  }

  const has = (x) => extras.includes(x);
  const grid = dense ? "grid-cols-1" : "sm:grid-cols-2";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid={tid("form")} noValidate>
      <div className={`grid gap-4 ${grid}`}>
        <Field label="First name *"><Input className={inputCls} value={f.first_name} onChange={set("first_name")} data-testid={tid("first-name")} autoComplete="given-name" /></Field>
        <Field label="Last name"><Input className={inputCls} value={f.last_name} onChange={set("last_name")} data-testid={tid("last-name")} autoComplete="family-name" /></Field>
        <Field label="Email *"><Input type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid={tid("email")} autoComplete="email" /></Field>
        <Field label="Phone"><Input type="tel" className={inputCls} value={f.phone} onChange={set("phone")} data-testid={tid("phone")} autoComplete="tel" /></Field>
        {has("direction") && (
          <Field label="Your move"><SimpleSelect value={f.move_direction} onChange={set("move_direction")} placeholder="Select" testId={tid("direction")}
            options={["PCS to Hampton Roads / Richmond", "PCS away (sell or rent my home)", "Retiring / separating here", "Local move"]} /></Field>
        )}
        {has("branch") && <Field label="Branch"><SimpleSelect value={f.branch} onChange={set("branch")} options={BRANCHES} placeholder="Select" testId={tid("branch")} /></Field>}
        {has("base") && <Field label="Installation"><BaseSelect value={f.base_id} onChange={set("base_id")} testId={tid("base")} /></Field>}
        {has("reportDate") && <Field label="Report / move date"><DateField value={f.report_date} onChange={set("report_date")} placeholder="When do you report?" testId={tid("report-date")} /></Field>}
        {has("preferredDate") && <Field label="Preferred tour date"><DateField value={f.preferred_date} onChange={set("preferred_date")} testId={tid("preferred-date")} /></Field>}
        {has("address") && <Field label="Property address" className={dense ? "" : "sm:col-span-2"}><Input className={inputCls} value={f.property_address || ""} onChange={set("property_address")} data-testid={tid("address")} /></Field>}
        {has("propertyType") && <Field label="Property type"><SimpleSelect value={f.property_type} onChange={set("property_type")} options={PROPERTY_TYPES} placeholder="Select one" testId={tid("property-type")} /></Field>}
      </div>
      <Field label="Message"><Textarea rows={dense ? 3 : 4} className="rounded-md border-input bg-white text-[15px]" placeholder={messagePlaceholder} value={f.message} onChange={set("message")} data-testid={tid("message")} /></Field>
      <label className="flex items-start gap-3 text-xs leading-relaxed text-neutral-500">
        <Checkbox checked={f.consent} onCheckedChange={(v) => set("consent")(Boolean(v))} className="mt-0.5" data-testid={tid("consent")} />
        <span>{CONSENT_TEXT}</span>
      </label>
      <button type="submit" disabled={busy} data-testid={tid("submit")}
        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white transition-colors hover:bg-neutral-800 disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel}
      </button>
    </form>
  );
};
